import _ from 'lodash';

import { PendingItemType } from '@appTypes/enums';
import type {
  AdvancementHistoryItem,
  AdvancementHistoryRequest,
  GetAdvancementImportsRequest,
  GetAdvancementImportsResponse,
} from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';

type BodyNoAdvancementType = Omit<
  AdvancementHistoryRequest['body'],
  'advancementType'
>;

interface BulkRequest extends Omit<AdvancementHistoryRequest, 'body'> {
  body: BodyNoAdvancementType;
}

export const pendingItemsApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint POST /advancements/advancementHistory
     *
     * Allow user to get a list of all advancement import files he uploaded.
     */
    getUnitAdvancementImports: builder.query<
      GetAdvancementImportsResponse,
      { params: GetAdvancementImportsRequest }
    >({
      query: arg => ({
        url: `/persons/advancementImports?organizationGuid=${arg.params.organizationGuid}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        return [
          {
            type: tagTypes.AdvancementImports,
            id: `${arg.params.organizationGuid}`,
          },
        ];
      },
    }),
    /**
     * @esbEndpoint POST /advancements/advancementHistory
     *
     * This is used only for the requirements section
     */
    getBulkAdvancementHistory: builder.query<
      AdvancementHistoryItem[],
      {
        advancementTypes: PendingItemType[];
        payload: BulkRequest;
      }
    >({
      queryFn: async (arg, _query, _options, baseQuery) => {
        const url = '/advancements/advancementHistory';
        const params = arg.payload.params;

        const requests = arg.advancementTypes.map(type => {
          const body: AdvancementHistoryRequest['body'] = {
            ...arg.payload.body,
            advancementType: type,
          };

          return baseQuery({
            url,
            method: 'POST',
            params,
            body,
          });
        });

        const items = (await Promise.all(requests)).flatMap(response => {
          const result = (response.data || []) as AdvancementHistoryItem[];
          return result;
        });

        const mergedItems = _.chain(items)
          .groupBy(item => item.userId)
          .map(items => {
            const first = items[0];
            return {
              ...first,
              recordedInformation: items.flatMap(
                item => item.recordedInformation,
              ),
            };
          })
          .value();

        return { error: undefined, data: mergedItems };
      },
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        const types = _.chain(arg.advancementTypes)
          .uniq()
          .sort()
          .join(',')
          .value();
        const statuses = _.chain(arg.payload.body.status)
          .uniq()
          .sort()
          .join(',')
          .value();

        return [
          {
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${arg.payload.body.organizationGuid}-${types}-${statuses}`,
          },
          {
            //what if only the above is ranks,award,mb
            type: tagTypes.AdvancementHistory,
            id: `Unit-List-${arg.payload.body.organizationGuid}-All-${statuses}`,
          },
        ];
      },
    }),
  }),
});

export const {
  useGetBulkAdvancementHistoryQuery,
  useLazyGetBulkAdvancementHistoryQuery,
  useGetUnitAdvancementImportsQuery,
  useLazyGetUnitAdvancementImportsQuery,
} = pendingItemsApi;
